import {Store} from 'vuex';
import {SnackbarAction} from '@/interfaces/clientOnly/snackbarAction';

/**
 * Global toast queue (snackbar, undo toast and the deprecated interrupt toast).
 *
 * - At most {@link MAX_TOASTS} toasts are stacked; the oldest one is closed when a new one arrives
 *   (a pending "delayed" undo toast then runs its commit, an interrupt toast continues its action).
 * - `$$showSnackbar` keeps its signatures; it renders as a message toast.
 * - `$$showUndo` shows "Rückgängig" for `timeout` ms:
 *   - optimistic: the action is already done, `undo` reverts it;
 *   - delayed: pass `commit`; it runs when the toast closes (timeout, ×, pushed out of the stack) unless undone.
 *     When the tab is closed or reloaded before that, the action is NOT executed (safe default: the pending
 *     timer simply dies with the page, nothing runs on `beforeunload`).
 * - `$$waitForInterrupt` (deprecated) delays an action; resolves `true` when the user cancelled it.
 *
 * Callbacks and timers live in this module (not in Vuex), only plain data is in the store.
 */

export const MAX_TOASTS = 3;
export const DEFAULT_SNACKBAR_TIMEOUT = 3000;
export const DEFAULT_UNDO_TIMEOUT = 6000;
export const DEFAULT_INTERRUPT_TIMEOUT = 3000;

export type ToastKind = 'message' | 'undo' | 'interrupt';

export interface ToastItem {
    id: number;
    kind: ToastKind;
    text: string;
    sub?: string;
    /** Legacy `btnColor` of the snackbar: success, error, warning, info, pink (default) … */
    color: string;
    /** ms, -1 = stays until closed */
    timeout: number;
    actions: SnackbarAction[];
    info?: any;
    /** Progress bar / timer paused (hover, hidden tab, details open) */
    paused: boolean;
    createdAt: number;
}

export interface UndoToastOptions {
    text: string;
    sub?: string;
    /** Reverts the action (optimistic mode) or just restores the UI (delayed mode) */
    undo: () => Promise<void> | void;
    /** Delayed mode: executes the action when the toast closes without "Rückgängig" */
    commit?: () => Promise<void> | void;
    /** ms, default 6000 */
    timeout?: number;
    /** Error text when commit or undo fails (default: generic German text) */
    errorText?: string;
}

/**
 * - `undone`: "Rückgängig" was clicked and `undo` succeeded
 * - `done`: toast closed without undo (and `commit` succeeded in delayed mode)
 * - `failed`: `undo` or `commit` threw; an error snackbar was shown
 */
export type UndoOutcome = 'undone' | 'done' | 'failed';

export type SnackbarInput = {
    text: string;
    btnColor?: string;
    timeout?: number;
    actions?: SnackbarAction[];
    info?: any;
};

type CloseReason = 'timeout' | 'close' | 'overflow' | 'undo' | 'abort' | 'action';

interface ToastHandlers {
    undo?: UndoToastOptions;
    resolveUndo?: (outcome: UndoOutcome) => void;
    resolveInterrupt?: (interrupted: boolean) => void;
}

interface TimerEntry {
    handle?: number;
    remaining: number;
    startedAt: number;
    reasons: Set<string>;
}

let boundStore: Store<any> | undefined;
let nextId = 1;
const handlers = new Map<number, ToastHandlers>();
const timers = new Map<number, TimerEntry>();

export function bindToastStore(store: Store<any>): void {
    boundStore = store;
    registerGlobalListeners();
}

/* ------------------------------- creation ------------------------------- */

function baseToast(kind: ToastKind, text: string, color: string, timeout: number): ToastItem {
    return {
        id: nextId++,
        kind,
        text,
        color,
        timeout,
        actions: [],
        paused: false,
        createdAt: Date.now(),
    };
}

export function createMessageToast(obj: SnackbarInput): ToastItem {
    const toast = baseToast('message', obj.text, obj.btnColor || 'pink', obj.timeout || DEFAULT_SNACKBAR_TIMEOUT);
    toast.actions = obj.actions || [];
    toast.info = obj.info;
    return toast;
}

/**
 * Adds the toast to the (reactive) list: replaces an identical visible message, closes the oldest toasts above
 * {@link MAX_TOASTS} and starts the timer. Must be called inside a mutation.
 */
export function enqueueToast(list: ToastItem[], toast: ToastItem): void {
    if (toast.kind === 'message') {
        const duplicate = list.findIndex(t => t.kind === 'message' && t.text === toast.text && t.color === toast.color);
        if (duplicate >= 0) {
            const [old] = list.splice(duplicate, 1);
            clearTimer(old.id);
        }
    }
    list.push(toast);
    while (list.length > MAX_TOASTS) {
        const evicted = list.shift();
        if (evicted) {
            clearTimer(evicted.id);
            // settle outside of the running mutation
            setTimeout(() => settle(evicted, 'overflow'), 0);
        }
    }
    startTimer(toast);
}

/** Removes all message toasts (legacy `hideSnackbar`). Must be called inside a mutation. */
export function removeMessageToasts(list: ToastItem[]): void {
    for (let i = list.length - 1; i >= 0; i--) {
        if (list[i].kind === 'message') {
            clearTimer(list[i].id);
            list.splice(i, 1);
        }
    }
}

/* -------------------------------- timers -------------------------------- */

function startTimer(toast: ToastItem) {
    if (toast.timeout < 0) {
        return;
    }
    const entry: TimerEntry = {remaining: toast.timeout, startedAt: Date.now(), reasons: new Set()};
    timers.set(toast.id, entry);
    if (isGloballyPaused(toast.kind)) {
        entry.reasons.add(document.hidden ? 'hidden' : 'blur');
        setPausedFlag(toast.id, true);
        return;
    }
    entry.handle = window.setTimeout(() => closeToast(toast.id, 'timeout'), entry.remaining);
}

function clearTimer(id: number) {
    const entry = timers.get(id);
    if (entry?.handle) {
        clearTimeout(entry.handle);
    }
    timers.delete(id);
}

function setPausedFlag(id: number, paused: boolean) {
    if (!boundStore) {
        return;
    }
    // Deferred: may be called while a mutation is running
    setTimeout(() => boundStore?.commit('patchToast', {id, patch: {paused}}), 0);
}

/** Pauses the timer of a toast for a reason (hover, details, hidden, blur) */
export function pauseToast(id: number, reason: string): void {
    const entry = timers.get(id);
    if (!entry) {
        return;
    }
    if (entry.reasons.size === 0 && entry.handle) {
        clearTimeout(entry.handle);
        entry.handle = undefined;
        entry.remaining = Math.max(0, entry.remaining - (Date.now() - entry.startedAt));
        setPausedFlag(id, true);
    }
    entry.reasons.add(reason);
}

export function resumeToast(id: number, reason: string): void {
    const entry = timers.get(id);
    if (!entry || !entry.reasons.delete(reason) || entry.reasons.size > 0) {
        return;
    }
    entry.startedAt = Date.now();
    // at least a short moment, so a toast does not vanish the instant the mouse leaves
    entry.handle = window.setTimeout(() => closeToast(id, 'timeout'), Math.max(entry.remaining, 800));
    setPausedFlag(id, false);
}

/** Remaining ms of a toast (for the progress bar after pauses) */
export function remainingMs(id: number): number | undefined {
    const entry = timers.get(id);
    if (!entry) {
        return undefined;
    }
    return entry.reasons.size > 0 || !entry.handle ? entry.remaining : Math.max(0, entry.remaining - (Date.now() - entry.startedAt));
}

function isGloballyPaused(kind: ToastKind): boolean {
    if (typeof document === 'undefined') {
        return false;
    }
    if (document.hidden) {
        return true;
    }
    // legacy: the snackbar paused while the window had no focus; actions only pause on a hidden tab
    return kind === 'message' && typeof document.hasFocus === 'function' && !document.hasFocus();
}

function currentToasts(): ToastItem[] {
    return (boundStore?.state.toasts || []) as ToastItem[];
}

function forEachToast(kinds: ToastKind[], fn: (toast: ToastItem) => void) {
    currentToasts().filter(t => kinds.includes(t.kind)).forEach(fn);
}

let listenersRegistered = false;

function registerGlobalListeners() {
    if (listenersRegistered || typeof window === 'undefined') {
        return;
    }
    listenersRegistered = true;
    document.addEventListener('visibilitychange', () => {
        const all: ToastKind[] = ['message', 'undo', 'interrupt'];
        forEachToast(all, t => (document.hidden ? pauseToast(t.id, 'hidden') : resumeToast(t.id, 'hidden')));
    });
    window.addEventListener('blur', () => forEachToast(['message'], t => pauseToast(t.id, 'blur')));
    window.addEventListener('focus', () => forEachToast(['message'], t => resumeToast(t.id, 'blur')));
}

/* ------------------------------- closing -------------------------------- */

/**
 * Closes a toast and runs what belongs to the reason:
 * undo toast: `undo` → undo callback, otherwise commit (delayed mode);
 * interrupt toast: `abort` → resolves true (cancelled), otherwise false (action continues).
 */
export function closeToast(id: number, reason: CloseReason): void {
    const toast = currentToasts().find(t => t.id === id);
    clearTimer(id);
    if (!toast) {
        return;
    }
    boundStore?.commit('removeToast', id);
    settle(toast, reason);
}

function settle(toast: ToastItem, reason: CloseReason) {
    const h = handlers.get(toast.id);
    handlers.delete(toast.id);
    if (!h) {
        return;
    }
    if (h.resolveInterrupt) {
        const interrupted = reason === 'abort';
        if (interrupted) {
            showMessage({text: 'Der Vorgang wurde abgebrochen.', btnColor: 'success'});
        }
        h.resolveInterrupt(interrupted);
        return;
    }
    if (h.undo && h.resolveUndo) {
        void settleUndo(h.undo, reason, h.resolveUndo);
    }
}

async function settleUndo(options: UndoToastOptions, reason: CloseReason, resolve: (outcome: UndoOutcome) => void) {
    if (reason === 'undo') {
        try {
            await options.undo();
            resolve('undone');
        } catch (e) {
            showError(options.errorText || 'Rückgängig machen ist fehlgeschlagen.', e);
            resolve('failed');
        }
        return;
    }
    if (!options.commit) {
        resolve('done');
        return;
    }
    try {
        await options.commit();
        resolve('done');
    } catch (e) {
        showError(options.errorText || 'Die Aktion konnte nicht ausgeführt werden.', e);
        resolve('failed');
    }
}

function errorInfo(error: any): any {
    if (!error) {
        return undefined;
    }
    if (error.isAxiosError) {
        return `${error.response?.status || error.code || ''} ${error.message}`.trim();
    }
    return error;
}

function showMessage(obj: SnackbarInput) {
    boundStore?.commit('showSnackbar', obj);
}

function showError(text: string, error: any) {
    showMessage({text, btnColor: 'error', info: errorInfo(error)});
}

/* --------------------------------- API ---------------------------------- */

/** `$$showUndo`: shows "Rückgängig" for `timeout` ms (default 6000) */
export function showUndoToast(options: UndoToastOptions): Promise<UndoOutcome> {
    return new Promise((resolve) => {
        const toast = baseToast('undo', options.text, 'success', options.timeout ?? DEFAULT_UNDO_TIMEOUT);
        toast.sub = options.sub;
        handlers.set(toast.id, {undo: options, resolveUndo: resolve});
        if (!boundStore) {
            void settleUndo(options, 'timeout', resolve);
            return;
        }
        boundStore.commit('pushToast', toast);
    });
}

/** `$$waitForInterrupt` (deprecated): resolves true when the user cancelled the action */
export function waitForInterruptToast(text: string, timeout?: number): Promise<boolean> {
    return new Promise((resolve) => {
        const toast = baseToast('interrupt', text, 'info', timeout || DEFAULT_INTERRUPT_TIMEOUT);
        handlers.set(toast.id, {resolveInterrupt: resolve});
        if (!boundStore) {
            resolve(false);
            return;
        }
        boundStore.commit('pushToast', toast);
    });
}

/** Latest open undo toast (for the keyboard shortcut) */
export function latestUndoToast(): ToastItem | undefined {
    const undoToasts = currentToasts().filter(t => t.kind === 'undo');
    return undoToasts[undoToasts.length - 1];
}

/** Icon and accent color of a toast from the legacy btnColor */
export function toastTone(toast: ToastItem): { icon: string; tone: 'success' | 'error' | 'warning' | 'info' } {
    if (toast.kind === 'interrupt') {
        return {icon: 'mdi-timer-sand', tone: 'info'};
    }
    switch (toast.color) {
        case 'success':
        case 'green':
            return {icon: 'mdi-check-circle-outline', tone: 'success'};
        case 'error':
        case 'red':
            return {icon: 'mdi-alert-circle-outline', tone: 'error'};
        case 'warning':
        case 'orange':
            return {icon: 'mdi-alert-outline', tone: 'warning'};
        default:
            return {icon: 'mdi-information-outline', tone: 'info'};
    }
}
